import {
  AfterViewChecked,
  Directive,
  ElementRef,
  inject,
} from '@angular/core';

/**
 * Splits text into words and whitespace. Only collapsible whitespace (space, tab, line breaks)
 * separates words, so a non-breaking space stays inside its word ("56,56 %", "1 250 Kč").
 */
const WORD_OR_WHITESPACE = /[^ \t\n\r\f]+|[ \t\n\r\f]+/g;
const WHITESPACE_ONLY = /^[ \t\n\r\f]+$/;

@Directive({
  selector: '[liberroRevealHtml]',
})
export class RevealHtmlDirective implements AfterViewChecked {
  private initialized = false;

  private readonly el = inject(ElementRef<HTMLElement>);

  public ngAfterViewChecked(): void {
    if (this.initialized || !this.el.nativeElement.textContent?.trim()) {
      return;
    }

    this.initialized = true;
    this.revealWords();
  }

  private revealWords(): void {
    const root = this.el.nativeElement;

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
    );

    const textNodes: Text[] = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }

    const parts = textNodes.map(node =>
      (node.textContent ?? '').match(WORD_OR_WHITESPACE) ?? []
    );

    const wordCount = parts.reduce(
      (count, parts) =>
        count + parts.filter(part => !WHITESPACE_ONLY.test(part)).length,
      0,
    );

    const totalDuration = 500;
    const delay = wordCount > 1
      ? totalDuration / wordCount
      : 0;

    let index = 0;
    const spans: HTMLSpanElement[] = [];

    textNodes.forEach((node, nodeIndex) => {
      const fragment = document.createDocumentFragment();

      for (const part of parts[nodeIndex]) {
        if (WHITESPACE_ONLY.test(part)) {
          fragment.appendChild(
            document.createTextNode(part),
          );
          continue;
        }

        const span = document.createElement('span');

        span.className = 'reveal-word';
        span.textContent = part;
        span.style.animationDelay = `${index * delay}ms`;

        fragment.appendChild(span);
        spans.push(span);

        index++;
      }

      node.replaceWith(fragment);
    });

    this.unwrapWhenRevealed(spans);
  }

  private unwrapWhenRevealed(spans: readonly HTMLSpanElement[]): void {
    if (spans.length === 0 || !('getAnimations' in Element.prototype)) {
      return;
    }

    const animations: Animation[] = spans.flatMap(
      (span: HTMLSpanElement): Animation[] => span.getAnimations(),
    );

    Promise.all(
      animations.map((animation: Animation): Promise<Animation> => animation.finished),
    )
      .then((): void => this.unwrapWords(spans));
  }

  private unwrapWords(spans: readonly HTMLSpanElement[]): void {
    const parents = new Set<Node>();

    for (const span of spans) {
      const parent: Node | null = span.parentNode;

      if (parent === null) {
        continue;
      }

      parents.add(parent);
      span.replaceWith(...Array.from(span.childNodes));
    }

    parents.forEach((parent: Node): void => parent.normalize());
  }
}